import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { toast, alert } from '../utils/toast';
import { Info, AlertTriangle, Globe, Plus, Edit2, Trash2 } from 'lucide-react';

interface CorsManagerProps {
  applicationId: string;
  clientId: string;
  currentOrigins: string[];
  isSandbox: boolean;
}

interface CorsRequest {
  id: string;
  origin: string;
  environment: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

export const CorsManager: React.FC<CorsManagerProps> = ({
  applicationId,
  clientId,
  currentOrigins,
  isSandbox,
}) => {
  const queryClient = useQueryClient();
  const [showAddFirst, setShowAddFirst] = useState(false);
  const [showEditFirst, setShowEditFirst] = useState(false);
  const [showRequestAdditional, setShowRequestAdditional] = useState(false);
  const [showRequests, setShowRequests] = useState(false);

  const [firstOrigin, setFirstOrigin] = useState('');
  const [editOrigin, setEditOrigin] = useState('');
  const [additionalOrigin, setAdditionalOrigin] = useState('');
  const [requestReason, setRequestReason] = useState('');

  // Fetch CORS requests
  const { data: corsRequests } = useQuery({
    queryKey: ['cors-requests', applicationId],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: CorsRequest[] }>(
        `/clients/${clientId}/applications/${applicationId}/cors/requests`
      );
      return response.data.data;
    },
    enabled: showRequests,
  });

  // Add first origin mutation
  const addFirstMutation = useMutation({
    mutationFn: async (origin: string) => {
      const response = await api.patch(
        `/clients/${clientId}/applications/${applicationId}/cors/first`,
        { origin }
      );
      return response.data;
    },
    onSuccess: (data) => {
      const message =
        data.warnings && data.warnings.length > 0
          ? `Origem CORS configurada com sucesso! ${data.warnings.join(', ')}`
          : 'Origem CORS configurada com sucesso!';
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['application'] });
      setShowAddFirst(false);
      setFirstOrigin('');
    },
    onError: (error) => {
      toast.error(`Falha ao configurar origem CORS: ${getErrorMessage(error)}`);
    },
  });

  // Edit first origin mutation
  const editFirstMutation = useMutation({
    mutationFn: async (data: { oldOrigin: string; newOrigin: string }) => {
      const response = await api.patch(
        `/clients/${clientId}/applications/${applicationId}/cors/edit`,
        data
      );
      return response.data;
    },
    onSuccess: (data) => {
      const message =
        data.warnings && data.warnings.length > 0
          ? `Origem CORS atualizada com sucesso! ${data.warnings.join(', ')}`
          : 'Origem CORS atualizada com sucesso!';
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['application'] });
      setShowEditFirst(false);
      setEditOrigin('');
    },
    onError: (error) => {
      toast.error(`Falha ao atualizar origem CORS: ${getErrorMessage(error)}`);
    },
  });

  // Request additional origin mutation
  const requestAdditionalMutation = useMutation({
    mutationFn: async (data: { origin: string; reason: string }) => {
      const response = await api.post(
        `/clients/${clientId}/applications/${applicationId}/cors/request`,
        data
      );
      return response.data;
    },
    onSuccess: (data) => {
      const message =
        data.info && data.info.length > 0
          ? `Pedido de origem CORS submetido com sucesso! ${data.info.join(', ')}`
          : 'Pedido de origem CORS submetido com sucesso!';
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['cors-requests', applicationId] });
      setShowRequestAdditional(false);
      setAdditionalOrigin('');
      setRequestReason('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Falha ao solicitar origem CORS');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (origin: string) => {
      const response = await api.delete(
        `/clients/${clientId}/applications/${applicationId}/cors/${encodeURIComponent(origin)}`
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Origem CORS revogada com sucesso');
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['application'] });
      queryClient.invalidateQueries({ queryKey: ['cors-requests', applicationId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Falha ao revogar origem CORS');
    },
  });

  const handleAddFirst = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstOrigin.trim()) {
      addFirstMutation.mutate(firstOrigin.trim());
    }
  };

  const handleEditFirst = (e: React.FormEvent) => {
    e.preventDefault();
    if (editOrigin.trim() && currentOrigins.length > 0) {
      editFirstMutation.mutate({
        oldOrigin: currentOrigins[0],
        newOrigin: editOrigin.trim(),
      });
    }
  };

  const handleRequestAdditional = (e: React.FormEvent) => {
    e.preventDefault();
    if (additionalOrigin.trim() && requestReason.trim()) {
      requestAdditionalMutation.mutate({
        origin: additionalOrigin.trim(),
        reason: requestReason.trim(),
      });
    }
  };

  const hasOrigins = currentOrigins && currentOrigins.length > 0;
  const canAddMore = currentOrigins && currentOrigins.length < 3;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary-500" />
            <span>Configuração de Origens CORS</span>
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Domínios autorizados a efetuar chamadas diretas a partir do navegador para esta aplicação.
          </p>
        </div>
        {hasOrigins && (
          <button
            onClick={() => setShowRequests(!showRequests)}
            className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
          >
            {showRequests ? 'Ocultar Pedidos' : 'Ver Pedidos'}
          </button>
        )}
      </div>

      {/* Current Origins */}
      {hasOrigins ? (
        <div className="space-y-2.5 mb-3">
          {currentOrigins.map((origin, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700/80"
            >
              <code className="text-xs font-mono text-slate-800 dark:text-slate-200">{origin}</code>
              <div className="flex items-center gap-3">
                {index === 0 && (
                  <button
                    onClick={() => {
                      setEditOrigin(origin);
                      setShowEditFirst(true);
                    }}
                    className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 inline-flex items-center gap-1"
                  >
                    <Edit2 className="w-3.5 h-3.5" /> Editar
                  </button>
                )}
                <button
                  onClick={async () => {
                    const res = await alert.confirm(
                      'Revogar Origem CORS?',
                      `Tem a certeza que deseja revogar a origem "${origin}"?`
                    );
                    if (res.isConfirmed) {
                      revokeMutation.mutate(origin);
                    }
                  }}
                  disabled={revokeMutation.isPending}
                  className="text-xs font-semibold text-rose-600 dark:text-rose-400 hover:text-rose-700 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Revogar
                </button>
              </div>
            </div>
          ))}
          {canAddMore && (
            <button
              onClick={() => setShowRequestAdditional(true)}
              className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline w-full text-left px-3 py-2 inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Solicitar Origem Adicional ({currentOrigins.length}/3)
            </button>
          )}
        </div>
      ) : (
        <div className="text-center py-6 bg-slate-50 dark:bg-slate-800/40 rounded-xl mb-3 border border-dashed border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
            Nenhuma origem CORS configurada
          </p>
          <button
            onClick={() => setShowAddFirst(true)}
            className="btn-base btn-primary btn-sm text-xs px-4 py-2"
          >
            Configurar Primeira Origem
          </button>
        </div>
      )}

      {/* CORS Requests List */}
      {showRequests && corsRequests && corsRequests.length > 0 && (
        <div className="mt-4 space-y-2">
          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
            Histórico de Pedidos CORS
          </h5>
          {corsRequests.map((request) => (
            <div
              key={request.id}
              className={`text-xs p-3 rounded-xl border ${
                request.status === 'pending'
                  ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60'
                  : request.status === 'approved'
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60'
                    : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800/60'
              }`}
            >
              <div className="flex justify-between items-start">
                <code className="text-slate-800 dark:text-slate-200 font-mono">
                  {request.origin}
                </code>
                <span
                  className={`badge-modern text-xs ${
                    request.status === 'pending'
                      ? 'badge-warning'
                      : request.status === 'approved'
                        ? 'badge-success'
                        : 'badge-error'
                  }`}
                >
                  {request.status === 'pending'
                    ? 'Pendente'
                    : request.status === 'approved'
                      ? 'Aprovado'
                      : 'Rejeitado'}
                </span>
              </div>
              {request.rejection_reason && (
                <p className="text-rose-700 dark:text-rose-400 mt-1">
                  Motivo: {request.rejection_reason}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add First Origin Modal */}
      {showAddFirst && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
              Configurar Primeira Origem CORS
            </h3>
            <form onSubmit={handleAddFirst}>
              <div className="mb-4">
                <label className="label-modern">URL de Origem *</label>
                <input
                  type="text"
                  value={firstOrigin}
                  onChange={(e) => setFirstOrigin(e.target.value)}
                  className="input-modern"
                  placeholder={isSandbox ? 'http://localhost:3000' : 'https://seudominio.com'}
                  required
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                  {isSandbox
                    ? 'Sandbox: HTTP e localhost são permitidos'
                    : 'Produção: Deve utilizar HTTPS e um domínio válido'}
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 rounded-xl p-3 mb-4">
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  <Info className="w-4 h-4 inline mr-1 text-blue-600 dark:text-blue-400" /> A sua
                  primeira origem é aprovada automaticamente. Origens adicionais requerem aprovação
                  administrativa.
                </p>
              </div>

              {addFirstMutation.isError && (
                <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 p-3">
                  <p className="text-sm text-rose-800 dark:text-rose-300">
                    {getErrorMessage(addFirstMutation.error)}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFirst(false);
                    setFirstOrigin('');
                  }}
                  className="btn-base btn-md btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={addFirstMutation.isPending}
                  className="btn-base btn-md btn-primary flex-1"
                >
                  {addFirstMutation.isPending ? 'A configurar...' : 'Configurar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit First Origin Modal */}
      {showEditFirst && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
              Editar Origem CORS
            </h3>
            <form onSubmit={handleEditFirst}>
              <div className="mb-4">
                <label className="label-modern">Origem Atual</label>
                <input
                  type="text"
                  value={currentOrigins[0]}
                  readOnly
                  className="input-modern opacity-70"
                />
              </div>

              <div className="mb-4">
                <label className="label-modern">Nova Origem *</label>
                <input
                  type="text"
                  value={editOrigin}
                  onChange={(e) => setEditOrigin(e.target.value)}
                  className="input-modern"
                  placeholder={isSandbox ? 'http://localhost:3000' : 'https://seudominio.com'}
                  required
                />
              </div>

              {editFirstMutation.isError && (
                <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 p-3">
                  <p className="text-sm text-rose-800 dark:text-rose-300">
                    {getErrorMessage(editFirstMutation.error)}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditFirst(false);
                    setEditOrigin('');
                  }}
                  className="btn-base btn-md btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editFirstMutation.isPending}
                  className="btn-base btn-md btn-primary flex-1"
                >
                  {editFirstMutation.isPending ? 'A atualizar...' : 'Atualizar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Request Additional Origin Modal */}
      {showRequestAdditional && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
              Solicitar Origem CORS Adicional
            </h3>
            <form onSubmit={handleRequestAdditional}>
              <div className="mb-4">
                <label className="label-modern">URL de Origem *</label>
                <input
                  type="text"
                  value={additionalOrigin}
                  onChange={(e) => setAdditionalOrigin(e.target.value)}
                  className="input-modern"
                  placeholder={isSandbox ? 'http://localhost:3001' : 'https://app.seudominio.com'}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="label-modern">Motivo para Origem Adicional *</label>
                <textarea
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  className="input-modern"
                  placeholder="Explique por que necessita desta origem adicional (mín. 10 caracteres)"
                  rows={3}
                  minLength={10}
                  maxLength={500}
                  required
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {requestReason.length}/500 caracteres
                </p>
              </div>

              <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-xl p-3 mb-4">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-600 dark:text-amber-400" />{' '}
                  Origens adicionais requerem aprovação administrativa.
                </p>
              </div>

              {requestAdditionalMutation.isError && (
                <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 p-3">
                  <p className="text-sm text-rose-800 dark:text-rose-300">
                    {getErrorMessage(requestAdditionalMutation.error)}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowRequestAdditional(false);
                    setAdditionalOrigin('');
                    setRequestReason('');
                  }}
                  className="btn-base btn-md btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={requestAdditionalMutation.isPending}
                  className="btn-base btn-md btn-primary flex-1"
                >
                  {requestAdditionalMutation.isPending ? 'A submeter...' : 'Submeter Pedido'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
